
import React, { useState } from 'react';
import { UseFormReturn, ControllerRenderProps } from 'react-hook-form';
import type { BlogPostFormData } from '@/services/blog/types';
import { FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, X } from 'lucide-react';

interface TagsManagerProps {
  form: UseFormReturn<BlogPostFormData>;
}

export const TagsManager: React.FC<TagsManagerProps> = ({ form }) => {
  const [newTag, setNewTag] = useState('');

  const addTag = () => {
    if (!newTag.trim()) return;

    const currentTags = form.getValues('tags');

    // Check if input contains commas for bulk addition
    if (newTag.includes(',')) {
      // Split by comma, trim each tag, and filter out empty strings
      const newTags = newTag
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0)
        .filter(tag => !currentTags.includes(tag)); // Remove duplicates

      if (newTags.length > 0) {
        form.setValue('tags', [...currentTags, ...newTags]);
        setNewTag('');
      }
    } else {
      // Single tag addition (existing logic)
      const trimmedTag = newTag.trim();
      if (!currentTags.includes(trimmedTag)) {
        form.setValue('tags', [...currentTags, trimmedTag]);
        setNewTag('');
      }
    }
  };

  const removeTag = (tagToRemove: string) => {
    const currentTags = form.getValues('tags');
    form.setValue('tags', currentTags.filter((tag: string) => tag !== tagToRemove));
  };

  return (
    <FormField
      control={form.control}
      name="tags"
      render={({ field }: { field: ControllerRenderProps<BlogPostFormData, 'tags'> }) => (
        <FormItem>
          <FormLabel>Tags</FormLabel>
          <div className="flex gap-2 mb-2">
            <Input
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              placeholder="Add tags (separate multiple tags with commas)..."
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addTag();
                }
              }}
            />
            <Button type="button" onClick={addTag} size="sm">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {field.value.map((tag: string, index: number) => (
              <Badge key={index} variant="secondary" className="flex items-center gap-1">
                {tag}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-4 w-4 p-0"
                  onClick={() => removeTag(tag)}
                >
                  <X className="h-3 w-3" />
                </Button>
              </Badge>
            ))}
          </div>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
